import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import PublicNavbar from "@/components/layout/PublicNavbar";
import HeroSection from "@/components/landing/HeroSection";
import { faqs } from "@/components/landing/FaqSection";

// Below-the-fold sections are code-split so the initial JS payload for
// mobile stays small and the hero LCP paints faster.
const AutonomyStrip = lazy(() => import("@/components/landing/AutonomyStrip"));
const MidCtaSection = lazy(() => import("@/components/landing/MidCtaSection"));
const AiDemoSection = lazy(() => import("@/components/landing/AiDemoSection"));
const FeaturesSection = lazy(() => import("@/components/landing/FeaturesSection"));
const HowItWorksFlowSection = lazy(() => import("@/components/landing/HowItWorksFlowSection"));
const ForWhomSection = lazy(() => import("@/components/landing/ForWhomSection"));
const WeekInZensioSection = lazy(() => import("@/components/landing/WeekInZensioSection"));
const PricingGlimpseSection = lazy(() => import("@/components/landing/PricingGlimpseSection"));
const TrustSection = lazy(() => import("@/components/landing/TrustSection"));
const SeoIntentSection = lazy(() => import("@/components/landing/SeoIntentSection"));
const StickyMobileCta = lazy(() => import("@/components/landing/StickyMobileCta"));
const FaqSection = lazy(() => import("@/components/landing/FaqSection"));
const ContactSection = lazy(() => import("@/components/landing/ContactSection"));
const CtaSection = lazy(() => import("@/components/landing/CtaSection"));
const FooterSection = lazy(() => import("@/components/landing/FooterSection"));

// Reserves vertical space while a lazy section resolves so lazy-loading
// cannot introduce layout shift below the hero.
const SectionFallback = ({ h = "min-h-[320px]" }: { h?: string }) => (
  <div aria-hidden className={h} />
);

export default function LandingPage() {
  return (
    <div className="min-h-screen relative bg-background">
      {/* Subtle paper texture / grid */}
      <div
        className="fixed inset-0 -z-10 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(var(--secondary)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--secondary)) 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <Helmet>
        <title>Zensio – AI-plattform för svenska företag | Testa gratis</title>
        <meta name="description" content="En AI som sköter sociala medier, sälj, marknad och kundservice – på svenska. Byggd för svenska företag. Testa gratis i 14 dagar – inget kort krävs." />
        <link rel="canonical" href="https://zensio.se/" />
        <meta property="og:url" content="https://zensio.se/" />
        <meta property="og:title" content="Zensio – AI-plattform för svenska företag" />
        <meta property="og:description" content="En AI som sköter sociala medier, sälj, marknad och kundservice – på svenska. Byggd för svenska företag. Testa gratis i 14 dagar – inget kort krävs." />
        <meta name="twitter:title" content="Zensio – AI-plattform för svenska företag" />
        <meta name="twitter:description" content="En AI som sköter sociala medier, sälj, marknad och kundservice – på svenska. Byggd för svenska företag. Testa gratis i 14 dagar – inget kort krävs." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "SoftwareApplication",
          "name": "Zensio", "url": "https://zensio.se",
          "applicationCategory": "BusinessApplication", "operatingSystem": "Web",
          "inLanguage": "sv-SE",
          "image": "https://zensio.se/og-image.png",
          "description": "AI-plattform för sociala medier, försäljning, marknadsföring, kundservice och analys — på svenska.",
          "featureList": [
            "AI-inlägg för Facebook, Instagram, LinkedIn, X och TikTok",
            "Smart schemaläggning per plattform",
            "Försäljningspipeline med AI-poäng",
            "AI-inkorg för kundservice",
            "Företagets minne (RAG-kunskapsbas)",
            "Fortnox-integration"
          ],
          "publisher": { "@type": "Organization", "name": "Zensio", "url": "https://zensio.se" },
          "offers": [
            { "@type": "Offer", "name": "Starter", "price": "499", "priceCurrency": "SEK", "url": "https://zensio.se/priser", "category": "SubscriptionMonthly", "availability": "https://schema.org/InStock" },
            { "@type": "Offer", "name": "Pro", "price": "1299", "priceCurrency": "SEK", "url": "https://zensio.se/priser", "category": "SubscriptionMonthly", "availability": "https://schema.org/InStock" },
            { "@type": "Offer", "name": "Byrå", "price": "2499", "priceCurrency": "SEK", "url": "https://zensio.se/priser", "category": "SubscriptionMonthly", "availability": "https://schema.org/InStock" }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "FAQPage",
          "mainEntity": faqs.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Hem", "item": "https://zensio.se/" }
          ]
        })}</script>
      </Helmet>

      <PublicNavbar
        extraLinks={[
          { href: "#features", label: "Moduler" },
          { href: "#demo", label: "Demo" },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <main>
        <HeroSection />
        <Suspense fallback={<SectionFallback h="min-h-[120px]" />}>
          <AutonomyStrip />
          <MidCtaSection />
          <div className="bg-surface"><FeaturesSection /></div>
          <ForWhomSection />
          <div className="bg-surface"><AiDemoSection /></div>
          <HowItWorksFlowSection />
          <div className="bg-surface"><WeekInZensioSection /></div>
          <div className="bg-surface"><PricingGlimpseSection /></div>
          <TrustSection />
          <div className="bg-surface"><SeoIntentSection /></div>
          <FaqSection />
          <div className="bg-surface"><ContactSection /></div>
          <CtaSection />
        </Suspense>
      </main>
      <Suspense fallback={null}>
        <FooterSection />
        <StickyMobileCta />
      </Suspense>
    </div>
  );
}
